import { Check, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { assistedOfferWhatsAppUrl } from '@shared/config';
import { Badge, buttonVariants } from '@/components/ui';
import { routes } from '@/lib/routes';
import { usePageMeta } from '@/lib/seo';

export function PricingPage() {
  const { t } = useTranslation(['marketing', 'common']);
  const whatsAppOfferUrl = assistedOfferWhatsAppUrl();
  const freeFeatures = t('marketing:pricing.free.features', { returnObjects: true }) as string[];
  const assistedFeatures = t('marketing:pricing.assisted.features', {
    returnObjects: true,
  }) as string[];
  const faqs = t('marketing:pricing.faqs', { returnObjects: true }) as Array<{
    q: string;
    a: string;
  }>;
  usePageMeta({
    title: t('common:seo.pricingTitle'),
    description: t('common:seo.pricingDescription'),
  });

  return (
    <div className="marketing-page section-pad">
      <div className="container">
        <div className="section-heading section-heading--center marketing-hero">
          <Badge tone="brand">
            <Sparkles size={14} /> {t('marketing:pricing.eyebrow')}
          </Badge>
          <h1>{t('marketing:pricing.title')}</h1>
          <p>{t('marketing:pricing.subtitle')}</p>
        </div>
        <div className="pricing-grid">
          <article className="pricing-card">
            <div className="pricing-card__header">
              <Badge tone="success">{t('marketing:pricing.free.name')}</Badge>
              <h2>{t('marketing:pricing.free.price')}</h2>
              <p>{t('marketing:pricing.free.period')}</p>
            </div>
            <p className="pricing-card__description">{t('marketing:pricing.free.description')}</p>
            <ul>
              {freeFeatures.map((item) => (
                <li key={item}>
                  <Check size={17} /> {item}
                </li>
              ))}
            </ul>
            <Link className={buttonVariants({ size: 'lg' })} to={routes.register}>
              {t('common:actions.startFree')}
            </Link>
          </article>
          <article className="pricing-card pricing-card--featured">
            <div className="pricing-card__header">
              <Badge tone="brand">{t('marketing:pricing.assisted.name')}</Badge>
              <p className="mt-4 text-sm text-slate-400 line-through">
                {t('marketing:pricing.assisted.oldPrice')}
              </p>
              <h2>{t('marketing:pricing.assisted.price')}</h2>
              <p>{t('marketing:pricing.assisted.period')}</p>
            </div>
            <p className="pricing-card__description">
              {t('marketing:pricing.assisted.description')}
            </p>
            <ul>
              {assistedFeatures.map((item) => (
                <li key={item}>
                  <Check size={17} /> {item}
                </li>
              ))}
            </ul>
            <a
              className={buttonVariants({ size: 'lg' })}
              href={whatsAppOfferUrl}
              target="_blank"
              rel="noreferrer"
            >
              {t('marketing:pricing.assisted.button')}
            </a>
            <p className="mt-3 text-xs leading-5 text-slate-500">
              {t('marketing:pricing.assisted.note')}
            </p>
          </article>
        </div>
        <p className="pricing-note">{t('marketing:pricing.betaNote')}</p>
        <section className="faq-section">
          <div className="section-heading">
            <h2>{t('marketing:pricing.faqTitle')}</h2>
          </div>
          <div className="faq-grid">
            {faqs.map((faq) => (
              <article key={faq.q}>
                <h3>{faq.q}</h3>
                <p>{faq.a}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default PricingPage;
